import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { FaCalendarAlt, FaMapMarkerAlt, FaClock, FaUsers, FaArrowRight, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import EventDetailPage from './EventDetailPage';
import { subscribeToEvents, Event } from '../../firebase/firestore';

const Container = styled.div`
  padding: ${theme.spacing.lg};
`;

const Header = styled.div`
  margin-bottom: ${theme.spacing.xl};
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: bold;
  color: ${theme.colors.secondary};
  margin-bottom: ${theme.spacing.md};
`;

const TabContainer = styled.div`
  display: flex;
  background: ${theme.colors.lightGray};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.xs};
  margin-bottom: ${theme.spacing.lg};
`;

const Tab = styled.button<{ active: boolean }>`
  flex: 1;
  padding: ${theme.spacing.md};
  border: none;
  background: ${props => props.active ? theme.colors.white : 'transparent'};
  color: ${props => props.active ? theme.colors.primary : theme.colors.mediumGray};
  font-weight: 600;
  cursor: pointer;
  border-radius: ${theme.borderRadius.sm};
  transition: all 0.3s ease;
`;

const EventCard = styled.div`
  background: ${theme.colors.white};
  border-radius: ${theme.borderRadius.lg};
  overflow: hidden;
  box-shadow: ${theme.shadows.md};
  margin-bottom: ${theme.spacing.lg};
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.lg};
  }
`;

const EventImage = styled.div`
  height: 200px;
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.secondary} 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.white};
  font-size: 3rem;
  position: relative;
`;

const EventBadge = styled.div<{ type: 'upcoming' | 'past' }>`
  position: absolute;
  top: ${theme.spacing.md};
  right: ${theme.spacing.md};
  background: ${props => props.type === 'upcoming' ? theme.colors.success : theme.colors.mediumGray};
  color: ${theme.colors.white};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  font-weight: bold;
  font-size: 0.8rem;
`;

const EventContent = styled.div`
  padding: ${theme.spacing.lg};
`;

const EventTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: bold;
  color: ${theme.colors.secondary};
  margin-bottom: ${theme.spacing.md};
`;

const EventDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.md};
`;

const EventDetail = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  color: ${theme.colors.mediumGray};
  font-size: 0.9rem;
`;

const EventDescription = styled.p`
  color: ${theme.colors.mediumGray};
  line-height: 1.6;
  margin-bottom: ${theme.spacing.md};
`;

const EventActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const EventButton = styled.button`
  background: ${theme.colors.primary};
  color: ${theme.colors.white};
  border: none;
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.md};
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  transition: all 0.3s ease;
  
  &:hover {
    background: ${theme.colors.secondary};
    transform: translateY(-2px);
  }
`;

const ViewAllButton = styled.button`
  background: ${theme.colors.white};
  color: ${theme.colors.primary};
  border: 2px solid ${theme.colors.primary};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.md};
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  transition: all 0.3s ease;
  
  &:hover {
    background: ${theme.colors.primary};
    color: ${theme.colors.white};
  }
`;

const CollapsibleSection = styled.div`
  margin-bottom: ${theme.spacing.xl};
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.lg};
  cursor: pointer;
  padding: ${theme.spacing.md};
  background: ${theme.colors.white};
  border-radius: ${theme.borderRadius.md};
  box-shadow: ${theme.shadows.sm};
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: bold;
  color: ${theme.colors.secondary};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const SectionContent = styled.div<{ isOpen: boolean }>`
  max-height: ${props => props.isOpen ? '2000px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing.xxl};
  color: ${theme.colors.mediumGray};
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: ${theme.spacing.lg};
  color: ${theme.colors.lightGray};
`;

const EmptyTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: ${theme.spacing.md};
  color: ${theme.colors.secondary};
`;

const EmptyDescription = styled.p`
  font-size: 1rem;
  line-height: 1.6;
`;

const EventsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'upcoming' | 'past'>('upcoming');
  const [upcomingExpanded, setUpcomingExpanded] = useState(false);
  const [pastExpanded, setPastExpanded] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    
    // Set up real-time listener for events
    const unsubscribe = subscribeToEvents((eventsData) => {
      setEvents(eventsData);
      setLoading(false);
    });

    // Cleanup listener on component unmount
    return () => unsubscribe();
  }, []);

  // Filter events by type
  const upcomingEvents = events.filter(event => event.type === 'upcoming');
  const pastEvents = events.filter(event => event.type === 'past');

  const handleEventClick = (eventId: string) => {
    setSelectedEventId(eventId);
  };

  const renderEvents = (events: Event[]) => {
    if (events.length === 0) {
      return (
        <EmptyState>
          <EmptyIcon>
            <FaCalendarAlt />
          </EmptyIcon>
          <EmptyTitle>No events found</EmptyTitle>
          <EmptyDescription>
            {activeTab === 'upcoming' 
              ? 'Check back later for upcoming events.' 
              : 'No past events to display.'
            }
          </EmptyDescription>
        </EmptyState>
      );
    }

    return events.map((event) => (
      <EventCard key={event.id} onClick={() => handleEventClick(event.id)}>
        <EventImage>
          <FaCalendarAlt />
          <EventBadge type={event.type}>
            {event.type === 'upcoming' ? 'Upcoming' : 'Past'}
          </EventBadge>
        </EventImage>
        <EventContent>
          <EventTitle>{event.title}</EventTitle>
          <EventDetails>
            <EventDetail>
              <FaCalendarAlt />
              {event.date.toLocaleDateString()}
            </EventDetail>
            <EventDetail>
              <FaClock />
              {event.time}
            </EventDetail>
            <EventDetail>
              <FaMapMarkerAlt />
              {event.location}
            </EventDetail>
            <EventDetail>
              <FaUsers />
              {event.currentAttendees.length}/{event.maxAttendees} attendees
            </EventDetail>
          </EventDetails>
          <EventDescription>{event.description}</EventDescription>
          <EventActions>
            <EventButton>
              View Details
              <FaArrowRight />
            </EventButton>
          </EventActions>
        </EventContent>
      </EventCard>
    ));
  };

  if (loading) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <div>Loading events...</div>
        </div>
      </Container>
    );
  }

  if (selectedEventId) {
    const event = events.find(e => e.id === selectedEventId);
    if (!event) return null;
    return <EventDetailPage event={event} onBack={() => setSelectedEventId(null)} />;
  }

  return (
    <Container>
      <Header>
        <Title>Events</Title>
        <TabContainer>
          <Tab 
            active={activeTab === 'upcoming'} 
            onClick={() => setActiveTab('upcoming')}
          >
            Upcoming Events
          </Tab>
          <Tab 
            active={activeTab === 'past'} 
            onClick={() => setActiveTab('past')}
          >
            Past Events
          </Tab>
        </TabContainer>
      </Header>

      <CollapsibleSection>
        <SectionHeader onClick={() => setUpcomingExpanded(!upcomingExpanded)}>
          <SectionTitle>
            <FaCalendarAlt />
            Upcoming Events
          </SectionTitle>
          {upcomingExpanded ? <FaChevronUp /> : <FaChevronDown />}
        </SectionHeader>
        <SectionContent isOpen={upcomingExpanded}>
          {renderEvents(upcomingEvents)}
          {upcomingEvents.length > 0 && (
            <ViewAllButton>
              View All Upcoming Events
              <FaArrowRight />
            </ViewAllButton>
          )}
        </SectionContent>
      </CollapsibleSection>

      <CollapsibleSection>
        <SectionHeader onClick={() => setPastExpanded(!pastExpanded)}>
          <SectionTitle>
            <FaCalendarAlt />
            Past Events
          </SectionTitle>
          {pastExpanded ? <FaChevronUp /> : <FaChevronDown />}
        </SectionHeader>
        <SectionContent isOpen={pastExpanded}>
          {renderEvents(pastEvents)}
          {pastEvents.length > 0 && (
            <ViewAllButton>
              View All Past Events
              <FaArrowRight />
            </ViewAllButton>
          )}
        </SectionContent>
      </CollapsibleSection>
    </Container>
  );
};

export default EventsPage;
